import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import Camera3DControls from "./Camera3DControls";
import { NoToneMapping } from "three";
import { cameraOriginalPosition } from "./Camera3DControls";
import FacesLoader from "./FacesLoaderDynamic"; // Multi-parts dynamique
import PauseScene from "./PauseScene";
import SceneLights from "./SceneLights";
import { PCFSoftShadowMap, SRGBColorSpace } from "three";
import { Box } from "@mui/material";
import { fetchSceneGlobalSettings } from "../../helpers/ThreeDataRequests";
import ProductSelectionDebugWidget from "./ProductSelectionDebugWidget";
import NoProductView from "./NoProductView";
import { VIEWER_TOP_OFFSET_PX, VIEWER_CANVAS_TOP_OFFSET_PX } from "./viewerLayout";

const waiterCss = `
  .waiter-3d-wrapper { position: absolute; left: 0; top: 0; right: 0; bottom: 0; }
  .waiter-3d, .waiter-3d:after { border-radius: 50%; width: 100px; height: 100px; }
  .waiter-3d { font-size: 10px; position: absolute; top: 50%; left: 50%; margin-top: -60px; margin-left: -60px; text-indent: -9999px; border-top: 10px solid rgba(204,204,204,0.75); border-right: 10px solid rgba(204,204,204,0.75); border-bottom: 10px solid rgba(204,204,204,0.75); border-left: 10px solid rgba(90,90,90,1); transform: translateZ(0); animation: waiter-3d-animation 1.1s infinite linear; }
  @keyframes waiter-3d-animation { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

const ThreeScene = ({
  modelFormData,
  materialFormData,
  children,
  hide3D,
  noMatchingProduct = false,
  allTextures,
  isMobile,
  onProductConfigLoaded,
  priceDebug = null,
}) => {
  // Affichage effectif du spinner + voile gris. DIFFÉRÉ par rapport au début du
  // chargement :
  // un rechargement rapide / en cache se termine avant le délai → on n'affiche
  // jamais le spinner et l'ancienne scène (figée) reste visible jusqu'au swap
  // atomique. Le spinner n'apparaît donc que pour les vrais chargements longs
  // (OTS). Au tout premier chargement, on l'affiche immédiatement (rien à
  // montrer encore).
  const [showSpinner, setShowSpinner] = useState(true);
  const [shouldResetCamera, setShouldResetCamera] = useState(true);
  const [propsLightSettings, setPropsLightSettings] = useState(true);
  const [globalSceneSettings, setGlobalSceneSettings] = useState(null);
  // 🆕 Centre de la bounding box des meshes (pour centrer la caméra)
  const [meshBboxCenter, setMeshBboxCenter] = useState(null);
  const [selectionDebug, setSelectionDebug] = useState(null);
  const contentRef = useRef(null);
  const isFirstLoad = useRef(true);
  const firstLoadDoneRef = useRef(false);
  const spinnerDelayTimerRef = useRef(null);

  // Délai avant l'apparition du spinner sur un RECHARGEMENT (pas le 1er load).
  // En dessous, le changement est perçu comme instantané et on évite tout flash.
  const SPINNER_DELAY_MS = 350;

  const setLoadingState = useCallback((loading) => {
    if (loading) {
      if (!firstLoadDoneRef.current) {
        // Premier chargement : rien à afficher → spinner immédiat.
        setShowSpinner(true);
      } else if (!spinnerDelayTimerRef.current) {
        // Rechargement : on diffère le spinner. S'il se termine avant le délai,
        // aucun spinner ne sera affiché (cf. branche `else` ci-dessous).
        spinnerDelayTimerRef.current = setTimeout(() => {
          spinnerDelayTimerRef.current = null;
          setShowSpinner(true);
        }, SPINNER_DELAY_MS);
      }
      return;
    }
    // Fin de chargement : annuler le spinner différé éventuel et tout masquer.
    if (spinnerDelayTimerRef.current) {
      clearTimeout(spinnerDelayTimerRef.current);
      spinnerDelayTimerRef.current = null;
    }
    firstLoadDoneRef.current = true;
    setShowSpinner(false);
  }, []);

  // Charger les settings globaux une seule fois au montage
  useEffect(() => {
    fetchSceneGlobalSettings()
      .then((settings) => setGlobalSceneSettings(settings));
  }, []);
      
  // Mettre à jour les lumières quand le matériau change (sans refetch des settings)
  useEffect(() => {
    setPropsLightSettings(materialFormData.color?.lights);
  }, [materialFormData]);


  // Dimensions "live" du cache (mm) utilisées pour cadrer la caméra.
  // ⚠️ On NE les passe PAS directement à Camera3DControls : sinon le zoom se
  // recadrerait dès la saisie d'une dimension, AVANT que le nouveau modèle soit
  // chargé (alors que la scène précédente est encore affichée, figée/grisée).
  // On en publie un snapshot `committedSize` au moment du commit (en même temps
  // que le centre `meshBboxCenter`), pour que zoom + centrage basculent
  // exactement quand le nouveau modèle s'affiche.
  const liveSize = useMemo(() => ({
    x: Number(modelFormData.sizeZ),
    y: Number(modelFormData.sizeX),
    z: Number(modelFormData.sizeY),
  }), [modelFormData.sizeZ, modelFormData.sizeX, modelFormData.sizeY]);
  const liveSizeRef = useRef(liveSize);
  liveSizeRef.current = liveSize;
  const [committedSize, setCommittedSize] = useState(liveSize);
  useEffect(() => {
    if (!document.getElementById("waiter3dstyle")) {
      const styleTag = document.createElement("style");
      styleTag.id = "waiter3dstyle";
      styleTag.textContent = waiterCss;
      document.head.appendChild(styleTag);
    }
  }, []);

  // Reset caméra UNIQUEMENT au premier chargement.
  // ⚠️ On NE pilote PLUS le loader ici. Activer le loader sur tout changement de
  // dimension provoquait un spinner infini quand le changement n'impacte pas les
  // dimensions du cache (ex. P2 dominé par P1) : FacesLoaderDynamic ne déclenche
  // alors aucun rechargement et ne rappelle jamais setIsLoading(false). C'est
  // désormais FacesLoaderDynamic (qui seul sait si un rechargement est réel) qui
  // pilote entièrement le loader (effet de pause + commit + cas "aucun produit").
  useEffect(() => {
    if (isFirstLoad.current) {
      setShouldResetCamera(true);
      isFirstLoad.current = false;
    }
  }, [
    modelFormData.sizeX,
    modelFormData.sizeY,
    modelFormData.sizeZ,
    modelFormData.frontHeight,
    modelFormData.sizeL,
    modelFormData.sizeH,
    modelFormData.sizeP1,
    modelFormData.sizeP2,
    modelFormData.pattern,
    modelFormData.type,
    modelFormData.wallmount,
    modelFormData.bottom,
    modelFormData.rear,
  ]);

  useEffect(() => () => {
    if (spinnerDelayTimerRef.current) {
      clearTimeout(spinnerDelayTimerRef.current);
    }
  }, []);

  // Auto-clear shouldResetCamera après un frame pour permettre de futurs resets (transition false→true)
  useEffect(() => {
    if (shouldResetCamera) {
      const raf = requestAnimationFrame(() => setShouldResetCamera(false));
      return () => cancelAnimationFrame(raf);
    }
  }, [shouldResetCamera]);

  const applyCanvasOverlay = (opacity = "0.5") => {
    if (contentRef.current) {
      contentRef.current.style.transition = "opacity 0.3s ease";
      contentRef.current.style.opacity = opacity;
    }
  };
  const removeCanvasOverlay = () => {
    if (contentRef.current) contentRef.current.style.opacity = "1";
  };
  // Grise le rendu en pause manuelle (hide3D) ou quand le spinner est affiché
  // (= chargement réellement long). Pour un rechargement rapide, showSpinner
  // reste false : l'ancienne scène reste pleinement visible jusqu'au swap
  // atomique, sans voile ni spinner (pas de "disparition" du placeholder).
  useEffect(() => {
    if (hide3D) applyCanvasOverlay("0.5");
    else if (showSpinner) applyCanvasOverlay("0.55");
    else removeCanvasOverlay();
  }, [hide3D, showSpinner]);

  // Callback quand tous les meshes sont chargés — reçoit la bounding box globale.
  // C'est LE signal de commit côté caméra : on publie ici, de façon atomique,
  // le centre ET le snapshot des dimensions (committedSize). Le recadrage du
  // zoom se fait donc en même temps que l'apparition du nouveau modèle.
  const handleAllFacesLoaded = useCallback((bboxInfo) => {
    setLoadingState(false);
    if (bboxInfo?.center) {
      setMeshBboxCenter(bboxInfo.center);
    }
    setCommittedSize(liveSizeRef.current);
  }, [setLoadingState]);

  const handleSelectionDebugChange = useCallback((data) => {
    setSelectionDebug(data);
  }, []);

  const mergedDebug = useMemo(() => {
    if (!selectionDebug) return null;
    return { ...selectionDebug, priceDebug };
  }, [selectionDebug, priceDebug]);

  return (
    <Box sx={{ position: "relative", top: `${VIEWER_TOP_OFFSET_PX}px`, width: "100%", maxWidth: 949, aspectRatio: "949 / 572" }}>
      <ProductSelectionDebugWidget debug={mergedDebug} />
      {noMatchingProduct ? (
        <NoProductView />
      ) : (
        <>
      {showSpinner && !hide3D && (
        <div
          style={{
            position: "absolute",
            top: "35%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            textAlign: "center",
          }}
        >
          <div className="waiter-3d-wrapper">
            <div className="waiter-3d"></div>
          </div>
        </div>
      )}

      <Box
        ref={contentRef}
        sx={{
          position: "relative",
          top: `${VIEWER_CANVAS_TOP_OFFSET_PX}px`,
          width: "100%",
          height: "100%",
        }}
      >
        <Canvas
          style={{ width: "100%", height: "100%" }}
          shadows
          dpr={[1, 2]}
          camera={{
            fov: 60,
            near: 0.1,
            far: 2000,
            position: cameraOriginalPosition.toArray(),
          }}
          gl={{
            powerPreference: "high-performance",
            antialias: true,
            alpha: true,
            precision: "highp",
          }}
          onCreated={({ camera, gl }) => {
            camera.position.copy(cameraOriginalPosition);
            camera.lookAt(0, 0, 0);
            gl.outputEncoding = SRGBColorSpace;
            gl.physicallyCorrectLights = false;
            gl.setPixelRatio(2);

            gl.toneMapping = NoToneMapping;
            gl.toneMappingExposure = 1.5;
            gl.shadowMap.enabled = true;
            gl.shadowMap.type = PCFSoftShadowMap;
            gl.setClearColor("white");
          }}
        >
          <Camera3DControls
            size={committedSize}
            autoRotate={!hide3D && !showSpinner}
            shouldResetCamera={shouldResetCamera}
            targetCenter={meshBboxCenter}
          />
          <FacesLoader
            modelFormData={modelFormData}
            materialFormData={materialFormData}
            hide3D={hide3D}
            setIsLoading={setLoadingState}
            onAllFacesLoaded={handleAllFacesLoaded}
            allTextures={allTextures}
            isMobile={isMobile}
            globalSceneSettings={globalSceneSettings}
            onProductConfigLoaded={onProductConfigLoaded}
            onSelectionDebugChange={handleSelectionDebugChange}
          />
          <PauseScene hide3D={hide3D} />
          <SceneLights isMobile={isMobile} propsLightSettings={propsLightSettings} />
        </Canvas>
      </Box>
        </>
      )}
      {children}
    </Box>
  );
};

export default ThreeScene;
